import { Component } from '@theme/component';

const STORAGE_KEY = 'ex-age-gate';

/** How long a confirmation lasts. Fixed rather than a setting — it is a legal
 *  policy decision, not something to tune per theme. */
const REMEMBER_DAYS = 30;

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal itself.
 * @property {HTMLElement} alert - The message revealed when the visitor declines.
 * @property {HTMLButtonElement} confirm - The confirm button, focused on open.
 * @property {HTMLInputElement} [remember] - Opt-out of persistence; absent when the setting hides it.
 */

/**
 * Expand Age Gate — blocks the site until the visitor confirms their age.
 *
 * Uses <dialog>.showModal(), which gives a focus trap, inert background and the
 * top layer for free. Escape is suppressed: a gate that dismisses itself is not
 * a gate. Declining reveals a message rather than redirecting, so the visitor can
 * correct a misclick.
 *
 * @extends {Component<Refs>}
 */
class ExAgeGate extends Component {
  requiredRefs = ['dialog', 'alert', 'confirm'];

  connectedCallback() {
    super.connectedCallback();

    // Escape would otherwise close the dialog and expose the site.
    this.refs.dialog.addEventListener('cancel', (event) => event.preventDefault());

    if (this.#isDesignMode) {
      this.#bindDesignMode();
      return;
    }

    if (this.#hasValidConfirmation()) return;

    this.#open();
  }

  /**
   * Closes the gate, storing the confirmation unless the visitor opted out.
   *
   * With "Remember me" unticked the gate returns on the next visit, so the
   * confirmation is deliberately not written at all.
   */
  confirm() {
    const remember = this.refs.remember?.checked ?? true;

    if (remember) {
      this.#write({ confirmed: true, expires: Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000 });
    }

    this.refs.dialog.close();
  }

  /**
   * Reveals the decline message and returns focus to it for screen readers.
   */
  reject() {
    this.refs.alert.hidden = false;
  }

  #open() {
    this.refs.dialog.showModal();
    this.refs.confirm.focus();
  }

  /**
   * Reads the stored confirmation, treating anything unparseable or expired as absent.
   *
   * @returns {boolean} Whether a confirmation is present and still valid.
   */
  #hasValidConfirmation() {
    const stored = this.#read();
    return Boolean(stored?.confirmed) && Number(stored.expires) > Date.now();
  }

  /**
   * @returns {{confirmed?: boolean, expires?: number} | null}
   */
  #read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      // Private mode, disabled storage, or corrupt value — show the gate.
      return null;
    }
  }

  /**
   * @param {{confirmed: boolean, expires: number}} value
   */
  #write(value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Storage unavailable: the visitor passes this page load but is asked again later.
    }
  }

  get #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  /**
   * In the theme editor the gate opens only while its section is selected, so the
   * merchant can style it without it blocking the rest of the editor.
   */
  #bindDesignMode() {
    const sectionId = this.closest('.shopify-section')?.id;

    document.addEventListener('shopify:section:select', (event) => {
      if (/** @type {HTMLElement} */ (event.target).id !== sectionId) return;
      if (!this.refs.dialog.open) this.#open();
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (/** @type {HTMLElement} */ (event.target).id !== sectionId) return;
      this.refs.dialog.close();
    });
  }
}

if (!customElements.get('ex-age-gate')) {
  customElements.define('ex-age-gate', ExAgeGate);
}
